'use client'

import { createContext, useContext } from 'react'
import { z } from 'zod'
import type { PropsWithChildren } from 'react'

import type { AuthInformation } from '@dafiti/authentication-sdk'
import type { WithCustomLink } from '@/components/DefaultLink'
import type { FormComponentType } from '@/components/DefaultForm'
import { SUPPORTED_LOCALES } from '@/i18n/messages'

export const FOOTER_TYPE_VALUES = ['general', 'specific'] as const

export type FooterType = (typeof FOOTER_TYPE_VALUES)[number]

const optionsSchema = z.object({
  locale: z.enum(SUPPORTED_LOCALES),
  baseDomain: z.string().min(1),
  secureDomain: z.string().min(1),
  cookieDomain: z.string().min(1),
  bffBaseUrl: z.string().min(1),
  apiGatewayBaseUrl: z.string().min(1),
  cacheTime: z
    .string()
    .optional()
    .default('5h')
    .describe('Cache time for the header data (e.g., "5h", "30m", "1d")'),
  store: z.string().default('dafiti-br').optional(),
  urls: z.object({
    login: z.string(),
    customer: z.string(),
    coupons: z.string(),
    cart: z.string(),
    wishlist: z.string(),
    logout: z.string(),
    help: z.string(),
  }),
  features: z.enum(['lean_catalog', 'search_suggestion']).array().optional(),
  footer: z
    .object({
      type: z.enum(FOOTER_TYPE_VALUES).default('general'),
    })
    .default({ type: 'general' })
    .optional(),
})

type LayoutRouter = {
  pathname?: string
  push: (path: string) => void
}

export type AppLayoutContextValue = WithCustomLink<{
  auth?: AuthInformation | null
  userAgent?: string | null | undefined
  options: z.infer<typeof optionsSchema>
  router?: LayoutRouter
  FormComponent?: FormComponentType
}>

export const AppLayoutContext = createContext<AppLayoutContextValue | null>(
  null,
)

export const useAppLayoutContext = (): AppLayoutContextValue => {
  const context = useContext(AppLayoutContext)
  if (!context) {
    throw new Error(
      'useAppLayoutContext must be used within a AppLayoutProvider',
    )
  }
  return context
}

export type AppLayoutProviderProps = PropsWithChildren<AppLayoutContextValue>

export const AppLayoutProvider = ({
  children,
  auth,
  userAgent,
  options,
  router,
  LinkComponent,
  FormComponent,
}: AppLayoutProviderProps) => {
  const result = optionsSchema.safeParse(options)
  if (!result.success) {
    throw new Error(
      `Invalid options provided to AppLayoutProvider: ${result.error.message}`,
    )
  }

  return (
    <AppLayoutContext.Provider
      value={{
        auth,
        userAgent,
        router,
        options: result.data,
        LinkComponent,
        FormComponent,
      }}
    >
      {children}
    </AppLayoutContext.Provider>
  )
}
